import { getAuthItem, setAuthItem } from "../services/authSessionStore";
import { isSystemAdministratorRole, isSuperAdminRole } from "./permissionUtils";
import { DEFAULT_LANDING_ROUTE } from "../constants/appRoutes";
import {
  isSponsorCroSession,
  resolveSponsorCroLandingRoute,
} from "./sponsorCroAccess";

const GATE_KEY = "credentialGate";
const SUPER_ADMIN_LANDING_ROUTE = "/superadmin/organisations";

export const needsTermsAcceptance = (userData) => {
  if (!userData) return false;
  if (userData.RequiresTermsAcceptance === true) return true;
  const status = String(userData.AccountStatus ?? "").trim();
  return status === "" || status === "0";
};

export const needsCredentialChange = (userData) => {
  if (!userData) return false;
  if (needsTermsAcceptance(userData)) return false;
  return (
    userData.RequiresCredentialChange === true ||
    userData.MustChangePassword === true ||
    userData.MustChangeAccessCode === true ||
    userData.PasswordExpired === true ||
    userData.AccessCodeExpired === true
  );
};

/** Forced change (admin reset, expiry, first login) — user already signed in; do not ask for current credential. */
export const shouldSkipOldCredentialValidation = (userData) => {
  if (!userData) return false;
  return needsCredentialChange(userData);
};

export const needsCredentialExpiryWarning = (userData) => {
  if (!userData || needsCredentialChange(userData)) return false;
  if (
    userData.IsExemptFromExpiryPolicy === true ||
    isSystemAdministratorRole(userData.Role || userData.Roles)
  ) {
    return false;
  }
  return userData.CredentialExpiryWarning === true;
};

export const buildCredentialExpiryWarningMessage = (userData) => {
  if (!userData) return "";

  const parts = [];
  if (userData.PasswordExpiringSoon) {
    const days = userData.DaysUntilPasswordExpiry;
    parts.push(
      days != null && days >= 0
        ? `Password expires in ${days} day${days === 1 ? "" : "s"}.`
        : "Your password is due to expire soon.",
    );
  }
  if (userData.AccessCodeExpiringSoon) {
    const days = userData.DaysUntilAccessCodeExpiry;
    parts.push(
      days != null && days >= 0
        ? `Access code expires in ${days} day${days === 1 ? "" : "s"}.`
        : "Your access code is due to expire soon.",
    );
  }

  if (parts.length === 0) {
    return "Your login credentials are due to expire soon. Please update them before access is blocked.";
  }

  return `${parts.join(" ")} Please change them now to avoid being locked out at expiry.`;
};

export const resolvePostLoginRoute = (userData, hasMultipleOrgs) => {
  if (needsTermsAcceptance(userData)) return "/accept-terms";
  if (needsCredentialChange(userData)) return "/change-credentials-required";
  if (hasMultipleOrgs) return "/organizations";
  if (isSuperAdminRole(userData?.Role || userData?.Roles)) {
    return SUPER_ADMIN_LANDING_ROUTE;
  }
  if (isSponsorCroSession(userData)) {
    return resolveSponsorCroLandingRoute(userData);
  }
  return DEFAULT_LANDING_ROUTE;
};

export const storeCredentialGateFromUser = (userData) => {
  if (!userData) return;
  const gate = needsTermsAcceptance(userData)
    ? "terms"
    : needsCredentialChange(userData)
      ? "credentials"
      : "none";
  setAuthItem(GATE_KEY, gate);
};

export const getCredentialGate = () => getAuthItem(GATE_KEY) || "none";

export const clearCredentialGate = () => setAuthItem(GATE_KEY, "none");

const CREDENTIAL_EXPIRY_BANNER_DISMISSED_KEY = "aq_credentialExpiryBannerDismissed";

export const isCredentialExpiryBannerDismissed = () => {
  try {
    return sessionStorage.getItem(CREDENTIAL_EXPIRY_BANNER_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
};

export const dismissCredentialExpiryBannerForSession = () => {
  try {
    sessionStorage.setItem(CREDENTIAL_EXPIRY_BANNER_DISMISSED_KEY, "1");
  } catch {
    // ignore private mode / quota
  }
};

export const clearCredentialExpiryBannerDismissForSession = () => {
  try {
    sessionStorage.removeItem(CREDENTIAL_EXPIRY_BANNER_DISMISSED_KEY);
  } catch {
    // ignore
  }
};

export const mergeCredentialStatusIntoUser = (userData, status) => {
  if (!userData || !status) return userData;
  return {
    ...userData,
    RequiresTermsAcceptance: status.requiresTermsAcceptance,
    RequiresCredentialChange: status.requiresCredentialChange,
    MustChangePassword: status.mustChangePassword,
    MustChangeAccessCode: status.mustChangeAccessCode,
    PasswordExpired: status.passwordExpired,
    AccessCodeExpired: status.accessCodeExpired,
    AccountStatus: status.accountStatus ?? userData.AccountStatus,
    CredentialExpiryWarning: status.credentialExpiryWarning,
    PasswordExpiringSoon: status.passwordExpiringSoon,
    AccessCodeExpiringSoon: status.accessCodeExpiringSoon,
    DaysUntilPasswordExpiry: status.daysUntilPasswordExpiry,
    DaysUntilAccessCodeExpiry: status.daysUntilAccessCodeExpiry,
    IsExemptFromExpiryPolicy: status.isExemptFromExpiryPolicy,
  };
};

export const PASSWORD_PATTERN = /^(?=.*[0-9])(?=.*[!@#$%^&*])[a-zA-Z0-9!@#$%^&*]{6,20}$/;

export const PASSWORD_REQUIREMENTS = [
  { key: "length", label: "6 to 20 characters" },
  { key: "number", label: "At least one number" },
  { key: "special", label: "At least one special character (!@#$%^&*)" },
];

export const getPasswordRequirementChecks = (password = "") => ({
  length: password.length >= 6 && password.length <= 20,
  number: /[0-9]/.test(password),
  special: /[!@#$%^&*]/.test(password),
});

export const ACCESS_CODE_REQUIREMENTS = [
  { key: "length", label: "Exactly 6 digits" },
  { key: "numeric", label: "Numbers only (0–9)" },
  { key: "match", label: "New and confirm access code match" },
];

export const getAccessCodeRequirementChecks = (code = "", confirmCode = "") => ({
  length: code.length === 6,
  numeric: code.length > 0 && /^\d+$/.test(code),
  match:
    code.length === 6 &&
    confirmCode.length === 6 &&
    code === confirmCode,
});
